import React from 'react';
import { Container } from 'vendors/semantic-ui';

import { readCMSValue, getDefault } from 'modules/shared-cms/utils';
import { buildComponentArray } from 'modules/shared/utils';

import { CMSImageContainer } from 'modules/shared-cms/components';
import {
    Description,
    SimpleStaggeredBlocList,
    VideoBloc,
    SelectionBloc,
    NewsProps,
    News,
    TitleBlocContainer,
    MentionContainer,
    AdvantageContainer,
} from 'modules/shared-cms-advanced/components';

import { PersonalizedAdviceProps } from './personalized-advice-type';

import enhancer from './personalized-advice-enhancer';

import styles from './personalized-advice-page.module.css';

const personalizedAdvicePage: React.FC<PersonalizedAdviceProps> = React.memo(
    ({
        titleBlocPicture,
        text: wrappedText,
        textFontSize: wrappedTextFontSize,
        textColor: wrappedTextColor,
        zoomedRibbonTitle,
        zoomedRibbonColor,
        zoomedRibbonTextColor,
        zoomedRibbonItems: wrappedZoomedRibbonList,
        videoLink: wrappedVideoLink,
        advantage: wrappedAdvantage,
        news: wrappedNews,
        logo: wrappedLogo,
        mention: wrappedMention,
        ...rest
    }: PersonalizedAdviceProps) => {
        const {
            text = getDefault(readCMSValue(wrappedText), ''),
            textFontSize = readCMSValue(wrappedTextFontSize),
            textColor = readCMSValue(wrappedTextColor),
            zoomedRibbonItems = getDefault(readCMSValue(wrappedZoomedRibbonList), []),
            vbVideoLinkUrl = getDefault(readCMSValue(wrappedVideoLink), ''),
            advantage = getDefault(readCMSValue(wrappedAdvantage), ''),
            logoPicture = getDefault(readCMSValue(wrappedLogo), []),
            mention = getDefault(readCMSValue(wrappedMention), ''),
        } = {};

        return (
            <React.Fragment>
                <div className={styles.container}>
                    {readCMSValue(titleBlocPicture) && (
                        <div className={styles.titleBlocSection}>
                            <TitleBlocContainer titleBlocPicture={titleBlocPicture} {...rest} />
                        </div>
                    )}

                    {text && (
                        <div className={styles.textSection}>
                            <Container>
                                <div className={styles.text}>
                                    <Description small text={text} fontSize={textFontSize} textColor={textColor} />
                                </div>
                            </Container>
                        </div>
                    )}

                    <SimpleStaggeredBlocList {...rest} />

                    {zoomedRibbonItems && zoomedRibbonItems.length > 0 && (
                        <div className={`${styles.zoomedRibbonBloc}`}>
                            <SelectionBloc
                                title={zoomedRibbonTitle}
                                color={zoomedRibbonColor}
                                textColor={zoomedRibbonTextColor}
                                items={zoomedRibbonItems}
                            />
                        </div>
                    )}

                    {vbVideoLinkUrl && (
                        <div className={styles.videoBloc}>
                            <Container>
                                <VideoBloc videoLink={vbVideoLinkUrl} {...rest} />
                            </Container>
                        </div>
                    )}

                    {advantage && (
                        <div className={styles.assetSection}>
                            <AdvantageContainer
                                advantage={advantage}
                                titleStyle={{ fontFamily: 'UrbanistExtraBold', fontSize: '1.8rem' }}
                                {...rest}
                            />
                        </div>
                    )}

                    {wrappedNews && (
                        <div>
                            <div className={styles.newsContainer}>
                                <div className={`${styles.newsContainer}`}>
                                    {buildComponentArray<NewsProps>(
                                        wrappedNews.iv,
                                        (itemProps: NewsProps, key: number): JSX.Element => {
                                            return (
                                                // eslint-disable-next-line react/no-array-index-key
                                                <div className={`${styles.newsWrapper}`} key={key}>
                                                    <News {...itemProps} />
                                                </div>
                                            );
                                        },
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    <div className={styles.logoContainer}>
                        <CMSImageContainer picture={logoPicture} style={{ margin: 'auto' }} />
                    </div>

                    {mention && (
                        <div className={styles.mentionSection}>
                            <Container>
                                <div className={styles.mentiontext}>
                                    <MentionContainer mention={mention} {...rest} />
                                </div>
                            </Container>
                        </div>
                    )}
                </div>
            </React.Fragment>
        );
    },
);

export const PersonalizedAdvicePage = enhancer(personalizedAdvicePage);
